import { useEffect } from 'react';
import './HeadSmashAnimation.css';

/**
 * PROFESSIONAL HEAD SMASH ANIMATION
 * Rock-type physical move - devastating headbutt with recoil
 * 
 * Key Features:
 * - Rock-type physical attack (150 base power, 80% accuracy)
 * - User takes 1/2 recoil damage
 * - Massive rocky headbutt charge with brutal impact
 * - Professional gaming quality with rock debris and heavy recoil
 */
export default function HeadSmashAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Rock Energy Buildup (0-400ms)
    playerEl.classList.add('head-smash-user-charge');

    const rockAura = document.createElement('div');
    rockAura.className = 'head-smash-rock-aura';
    rockAura.style.left = `${playerX - 70}px`;
    rockAura.style.top = `${playerY - 70}px`;
    container.appendChild(rockAura);
    elements.push(rockAura);

    for (let i = 0; i < 8; i++) {
      const rock = document.createElement('div');
      rock.className = 'head-smash-charge-rock';
      const angle = (i / 8) * 360;
      const radius = 55;
      rock.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 5}px`;
      rock.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 5}px`;
      rock.style.animationDelay = `${i * 40}ms`;
      container.appendChild(rock);
      elements.push(rock);
    }

    // PHASE 2: Reckless Charge (400-800ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('head-smash-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.85}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.85}px`);

      // Rock debris trail
      for (let i = 0; i < 10; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'head-smash-trail-rock';
          const progress = i / 10;
          trail.style.left = `${playerX + dx * progress * 0.85 + (Math.random() - 0.5) * 20 - 5}px`;
          trail.style.top = `${playerY + dy * progress * 0.85 + (Math.random() - 0.5) * 20 - 5}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 30);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Devastating Impact (800-1300ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('head-smash-opponent-hit');
      playerEl.classList.remove('head-smash-user-dash');

      // Massive rock explosion
      const explosion = document.createElement('div');
      explosion.className = 'head-smash-explosion';
      explosion.style.left = `${opponentX - 120}px`;
      explosion.style.top = `${opponentY - 120}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Rock shard burst
      for (let i = 0; i < 22; i++) {
        const shardAngle = (i / 22) * 360;
        const shardDist = 65 + Math.random() * 55;
        const shard = document.createElement('div');
        shard.className = 'head-smash-shard';
        shard.style.left = `${opponentX - 5}px`;
        shard.style.top = `${opponentY - 5}px`;
        shard.style.setProperty('--scatter-x', `${Math.cos(shardAngle * Math.PI / 180) * shardDist}px`);
        shard.style.setProperty('--scatter-y', `${Math.sin(shardAngle * Math.PI / 180) * shardDist - 15}px`);
        shard.style.animationDelay = `${i * 12}ms`;
        container.appendChild(shard);
        elements.push(shard);
      }

      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'head-smash-shockwave';
          sw.style.left = `${opponentX - 80}px`;
          sw.style.top = `${opponentY - 80}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 800);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Heavy Recoil (1300-1800ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('head-smash-user-charge');
      playerEl.classList.add('head-smash-user-recoil');

      const recoilFlash = document.createElement('div');
      recoilFlash.className = 'head-smash-recoil-flash';
      recoilFlash.style.left = `${playerX - 50}px`;
      recoilFlash.style.top = `${playerY - 50}px`;
      container.appendChild(recoilFlash);
      elements.push(recoilFlash);

      for (let i = 0; i < 8; i++) {
        const recoilRock = document.createElement('div');
        recoilRock.className = 'head-smash-recoil-rock';
        const angle = (i / 8) * 360;
        const dist = 30 + Math.random() * 20;
        recoilRock.style.left = `${playerX - 4}px`;
        recoilRock.style.top = `${playerY - 4}px`;
        recoilRock.style.setProperty('--scatter-x', `${Math.cos(angle * Math.PI / 180) * dist}px`);
        recoilRock.style.setProperty('--scatter-y', `${Math.sin(angle * Math.PI / 180) * dist}px`);
        container.appendChild(recoilRock);
        elements.push(recoilRock);
      }
    }, 1300);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('head-smash-user-charge', 'head-smash-user-dash', 'head-smash-user-recoil');
      opponentEl.classList.remove('head-smash-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1800);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('head-smash-user-charge', 'head-smash-user-dash', 'head-smash-user-recoil');
      opponentEl.classList.remove('head-smash-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

HeadSmashAnimation.moveType = 'rock';
